import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ChevronDown, ChevronRight, Save, Bookmark, Trash2,
  ArrowUp, ArrowDown, GripVertical, Lock, Unlock,
} from "lucide-react";
import { countWords } from "./helpers";
import { inferKind } from "./tocNumbering";
import { RichBodyEditor } from "@/components/manual/RichBodyEditor";

// -----------------------------------------------------------------------------
// Chapter card — one row of the manual chapter composer.
// Fields: Title, Subtitle, Description (goes into TOC), Content (chapter body).
// Supports HTML5 drag-and-drop reordering via the grip handle on the left,
// and chapter-scoped presets (save/load a chapter as a reusable template).
// -----------------------------------------------------------------------------
export function ChapterCard({
  index, chapter, isFirst, isLast, presets,
  onChange, onMove, onRemove, onSavePreset, onDeletePreset,
  isDragging, isDragTarget,
  onDragStart, onDragEnter, onDragEnd, onDrop,
  // iter-36: derived label from the parent — one of "Chapter 3" / "Part II"
  // / "" (sections have no label). Falls back to the row index if the
  // parent doesn't pass it (unit tests, older callers).
  computedLabel = "",
  // iter-53: forward the book's paste style mode into the rich editor
  // so pasted styles behave the same as inside the Upload Draft WYSIWYG.
  permissive = false,
  // iter-54: bookId is needed for the RichBodyEditor's image-insert
  // path (POST /api/books/{bookId}/inline-image).
  bookId = "",
  // iter-70: lock state + toggle. When ``locked`` is truthy every editable
  // control on this row is disabled — the author must unlock first. Only
  // wired up for chapters that came from a DOCX import (they have a
  // ``chapter_source_index`` the backend can key on).
  locked = false,
  onToggleLock,
  // iter-71 — inline reclassify to front / back matter. When the row is
  // a real chapter (not a part / section) and the callback is provided,
  // a small "Move to →" menu appears next to the type selector so authors
  // can reclassify without leaving Manual Entry.
  onReclassifyToFrontBack,
}) {
  // iter-109 — Rows added via "Add preface" / "Add appendix" /
  // "Add chapter" start pre-expanded so the author sees the body
  // RichBodyEditor immediately (the collapsed card previously made
  // it look like appendices had no editor at all — user screenshot).
  const [open, setOpen] = useState(!!chapter._autoOpen);
  const [showPresetSave, setShowPresetSave] = useState(false);
  const [presetName, setPresetName] = useState("");
  const wordCount = countWords(chapter.content);
  const kind = inferKind(chapter);
  // Left-hand numeric pill. For Parts / Sections show the kind letter
  // instead of the meaningless row index so the column stops mixing
  // "01, 02, 03, 04" with a part row that isn't chapter 1.
  const badge = kind === "part" ? "P"
              : kind === "section" ? "§"
              : kind === "appendix" ? ""
              : kind === "preface" ? "℘"
              : (computedLabel ? computedLabel.replace(/^Chapter\s+/i, "").padStart(2, "0")
                               : String(index + 1).padStart(2, "0"));

  const chapterPresets = useMemo(
    () => (presets || []).filter((p) => p.kind === "chapter"),
    [presets]
  );

  const applyPreset = (p) => {
    onChange({
      title: p.title || chapter.title,
      subtitle: p.subtitle || "",
      description: p.description || "",
      content: p.content || "",
    });
    setOpen(true);
    toast.success(`Loaded chapter preset: ${p.name}`);
  };

  const handleSavePreset = async () => {
    const saved = await onSavePreset(presetName);
    if (saved) { setPresetName(""); setShowPresetSave(false); }
  };

  // dragOver must preventDefault so drop fires; use "move" cursor while dragging.
  const handleDragOver = (e) => {
    if (isDragging) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const borderTop = isDragTarget ? "border-t-2 border-t-[color:var(--primary)]" : "";
  const opacity = isDragging ? "opacity-40" : "";

  return (
    <div
      className={`border border-[color:var(--border)] bg-white transition-opacity ${borderTop} ${opacity}`}
      data-testid={`mc-chapter-${index}`}
      onDragOver={handleDragOver}
      onDragEnter={onDragEnter}
      onDrop={(e)=>{ e.preventDefault(); onDrop && onDrop(); }}
    >
      <div className="flex items-center gap-2 p-3">
        <span
          draggable
          onDragStart={(e)=>{
            e.dataTransfer.effectAllowed = "move";
            // Firefox requires setData for drag to fire; Safari private mode
            // sometimes blocks it — either failure is safe to swallow because
            // the drop handler falls back to component state (`dragIdx`).
            try { e.dataTransfer.setData("text/plain", String(index)); }
            catch (err) { console.debug("dataTransfer.setData blocked:", err?.message); }
            onDragStart && onDragStart();
          }}
          onDragEnd={onDragEnd}
          className="cursor-grab active:cursor-grabbing p-1 text-[color:var(--text-muted)] hover:text-[color:var(--text)]"
          title="Drag to reorder"
          data-testid={`mc-chapter-${index}-drag`}
        >
          <GripVertical className="w-4 h-4"/>
        </span>
        <button type="button" onClick={()=>setOpen(!open)}
          className="text-[color:var(--text-muted)] hover:text-[color:var(--text)]"
          aria-label={open ? "Collapse chapter" : "Expand chapter"}
          data-testid={`mc-chapter-${index}-toggle`}>
          {open ? <ChevronDown className="w-4 h-4"/> : <ChevronRight className="w-4 h-4"/>}
        </button>
        <span
          className={`text-xs font-mono w-6 text-center ${kind === "chapter" ? "text-[color:var(--text-muted)]" : "text-[color:var(--primary)] font-bold"}`}
          title={kind === "part" ? "Part row (Roman-numbered in TOC)"
               : kind === "section" ? "Section row (un-numbered TOC entry)"
               : kind === "appendix" ? "Appendix / labeless row (no prefix in TOC)"
               : kind === "preface" ? "Preface / front-matter row (renders before Chapter 1, no prefix)"
               : "Chapter row"}
          data-testid={`mc-chapter-${index}-badge`}
          data-kind={kind}
        >{badge}</span>
        <input value={chapter.title}
          onChange={(e)=>onChange({ title: e.target.value })}
          disabled={locked}
          placeholder={kind === "part" ? "Part title (e.g. Part I: Understanding …)"
                     : kind === "section" ? "Section title (e.g. Resources)"
                     : kind === "appendix" ? "Appendix title (e.g. Notes on Sources)"
                     : kind === "preface" ? "Preface title (e.g. How to Use This Book)"
                     : "Chapter title"}
          className={`flex-1 border border-[color:var(--border)] px-2 py-1 text-sm focus:outline-none focus:border-[color:var(--primary)] ${
            locked ? "bg-slate-100 text-slate-500 cursor-not-allowed" : ""
          }`}
          title={locked ? "Chapter is locked — unlock to edit the title" : undefined}
          data-testid={`mc-chapter-${index}-title`}/>
        {(chapter.label || "").trim() && (
          /* iter-38: visible chip on the collapsed row so authors see
             a label override without expanding. Click to expand. */
          <button
            type="button"
            onClick={()=>setOpen(true)}
            className="text-[10px] px-2 py-0.5 border border-[color:var(--primary)] text-[color:var(--primary)] uppercase tracking-widest whitespace-nowrap"
            data-testid={`mc-chapter-${index}-label-badge`}
            title="Label override — click to edit"
          >{chapter.label.trim()}</button>
        )}
        <span className="text-xs text-[color:var(--text-muted)] whitespace-nowrap font-mono">
          {wordCount.toLocaleString()} w
        </span>
        {chapterPresets.length > 0 && (
          <ChapterPresetPicker
            testId={`mc-chapter-${index}-presets`}
            presets={chapterPresets}
            onPick={applyPreset}
            onDelete={onDeletePreset}
          />
        )}
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* iter-71 — Front/Back matter reclassify. Only shown on true
              chapters (Parts and Sections aren't reclassifiable this
              way) and only when the row isn't locked. */}
          {onReclassifyToFrontBack && kind === "chapter" && !locked && (
            <details className="relative">
              <summary
                className="list-none cursor-pointer p-1 text-[color:var(--text-muted)] hover:text-[color:var(--text)]"
                title="Move to Front matter / Back matter"
                data-testid={`mc-chapter-${index}-reclassify-toggle`}
              >
                <span className="text-[10px] uppercase tracking-widest">Move →</span>
              </summary>
              <div
                className="absolute right-0 top-full mt-1 z-30 min-w-[180px] bg-white border border-[color:var(--border)] shadow-lg"
                data-testid={`mc-chapter-${index}-reclassify-menu`}
              >
                <button
                  type="button"
                  onClick={()=>onReclassifyToFrontBack("front_matter")}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-[color:var(--canvas)]"
                  data-testid={`mc-chapter-${index}-move-front-matter`}
                >
                  Front matter
                </button>
                <button
                  type="button"
                  onClick={()=>onReclassifyToFrontBack("back_matter")}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-[color:var(--canvas)]"
                  data-testid={`mc-chapter-${index}-move-back-matter`}
                >
                  Back matter
                </button>
              </div>
            </details>
          )}
          <button type="button" onClick={()=>onMove(-1)} disabled={isFirst || locked}
            className="p-1 text-[color:var(--text-muted)] hover:text-[color:var(--text)] disabled:opacity-30"
            title={locked ? "Unlock to move" : "Move up"} data-testid={`mc-chapter-${index}-up`}>
            <ArrowUp className="w-4 h-4"/>
          </button>
          <button type="button" onClick={()=>onMove(1)} disabled={isLast || locked}
            className="p-1 text-[color:var(--text-muted)] hover:text-[color:var(--text)] disabled:opacity-30"
            title={locked ? "Unlock to move" : "Move down"} data-testid={`mc-chapter-${index}-down`}>
            <ArrowDown className="w-4 h-4"/>
          </button>
          {/* iter-70/80 — Lock / Unlock. Wired for every row. Imported
              chapters key on ``chapter_source_index``; everything else
              (Parts, sections, back-matter, manual-only rows) keys on
              a stable ``row:{title}`` synthetic key handled by the
              row-lock endpoint. */}
          {onToggleLock && (
            <button type="button"
              onClick={()=>{
                const sidx = chapter.chapter_source_index;
                const lockRef = (sidx != null)
                  ? sidx
                  : `row:${(chapter.title || "").trim() || `idx-${index}`}`;
                onToggleLock(lockRef, !locked);
              }}
              className={`p-1 transition-colors ${
                locked
                  ? "text-[color:var(--primary)]"
                  : "text-[color:var(--text-muted)] hover:text-[color:var(--text)]"
              }`}
              title={locked
                ? "Unlock — re-enable editing on this row"
                : "Lock — pin this row and block edits"}
              data-testid={`mc-chapter-${index}-lock`}>
              {locked
                ? <Lock className="w-4 h-4" strokeWidth={2}/>
                : <Unlock className="w-4 h-4" strokeWidth={1.5}/>}
            </button>
          )}
          <button type="button"
            onClick={()=>{
              if (locked) {
                toast.error("Unlock this chapter first to delete it.");
                return;
              }
              if (window.confirm("Delete this chapter?")) onRemove();
            }}
            className={`p-1 ${locked ? "text-slate-300 cursor-not-allowed" : "text-[color:var(--text-muted)] hover:text-red-600"}`}
            title={locked ? "Unlock to delete" : "Delete chapter"}
            data-testid={`mc-chapter-${index}-delete`}>
            <Trash2 className="w-4 h-4"/>
          </button>
        </div>
      </div>
      {open && (
        <div className="px-3 pb-3 pl-12 space-y-2 border-t border-[color:var(--border)] bg-[color:var(--canvas)]/40 pt-3">
          {/* iter-36: kind + optional label override — controls how this
              row is numbered / styled in the TOC and exports. */}
          <div className="flex flex-wrap gap-3 items-end">
            <label className="block">
              <span className="block text-[10px] uppercase tracking-widest mb-1 text-[color:var(--text-muted)]">Type</span>
              <select
                value={kind}
                onChange={(e)=>onChange({ kind: e.target.value })}
                className="border border-[color:var(--border)] px-2 py-1 text-sm bg-white focus:outline-none focus:border-[color:var(--primary)]"
                data-testid={`mc-chapter-${index}-kind`}
              >
                <option value="chapter">Chapter (numbered)</option>
                <option value="part">Part (Roman divider, not counted)</option>
                <option value="section">Section (un-numbered TOC entry)</option>
                <option value="preface">Preface (front-matter, no prefix)</option>
                <option value="appendix">Appendix (blank label — no prefix)</option>
              </select>
            </label>
            <label className="block flex-1 min-w-[220px]">
              <span className="block text-[10px] uppercase tracking-widest mb-1 text-[color:var(--text-muted)]">
                Label override (optional)
              </span>
              <input value={chapter.label || ""}
                onChange={(e)=>onChange({ label: e.target.value })}
                placeholder={
                  kind === "part" ? "e.g. Part I (auto-generated if blank)"
                  : kind === "section" ? "e.g. Resources"
                  : kind === "appendix" ? "Optional — leave blank so the TOC shows only your title"
                  : kind === "preface" ? "Optional — leave blank so the TOC shows only your title"
                  : `e.g. Bonus Chapter — leave blank for auto “${computedLabel || `Chapter ${index+1}`}”`
                }
                className="w-full border border-[color:var(--border)] px-2 py-1 text-sm focus:outline-none focus:border-[color:var(--primary)]"
                data-testid={`mc-chapter-${index}-label`}/>
              {/* iter-38: one-click label presets. Chapter kind gets
                  quick "bonus / interlude / prologue / epilogue /
                  companion" chips; Parts get the current auto label as
                  a "reset" chip; Sections get common back-matter names. */}
              <div className="mt-1 flex flex-wrap gap-1" data-testid={`mc-chapter-${index}-label-presets`}>
                {(kind === "chapter"
                    ? ["Bonus Chapter", "Interlude", "Prologue", "Epilogue", "Companion Chapter"]
                    : kind === "section"
                      ? ["Resources", "Appendix", "Glossary", "Notes", "About the Author"]
                      : kind === "appendix"
                        ? ["Appendix A", "Appendix B", "Notes", "Acknowledgments", "Further Reading"]
                        : kind === "preface"
                          ? ["Preface", "Foreword", "Author's Note", "Translator's Note", "How to Use This Book"]
                          : [computedLabel || `Part ${index + 1}`]
                  ).map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={()=>onChange({ label: preset })}
                    className="text-[10px] px-2 py-0.5 border border-[color:var(--border)] bg-white hover:border-[color:var(--primary)] hover:text-[color:var(--primary)] uppercase tracking-widest"
                    data-testid={`mc-chapter-${index}-label-preset-${preset.toLowerCase().replace(/\s+/g,'-')}`}
                  >{preset}</button>
                ))}
                {(chapter.label || "").trim() && (
                  <button
                    type="button"
                    onClick={()=>onChange({ label: "" })}
                    className="text-[10px] px-2 py-0.5 border border-transparent text-[color:var(--text-muted)] hover:text-red-600 uppercase tracking-widest"
                    data-testid={`mc-chapter-${index}-label-clear`}
                    title="Clear override — use auto label"
                  >Clear</button>
                )}
              </div>
            </label>
          </div>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-widest mb-1 text-[color:var(--text-muted)]">Subtitle (optional)</span>
            <input value={chapter.subtitle} onChange={(e)=>onChange({ subtitle: e.target.value })}
              placeholder="A brief companion line under the title"
              className="w-full border border-[color:var(--border)] px-2 py-1 text-sm focus:outline-none focus:border-[color:var(--primary)]"
              data-testid={`mc-chapter-${index}-subtitle`}/>
          </label>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-widest mb-1 text-[color:var(--text-muted)]">
              TOC description (optional) — appears under the chapter entry in the Table of Contents
            </span>
            <input value={chapter.description || ""}
              onChange={(e)=>onChange({ description: e.target.value })}
              placeholder="e.g. Why racing thoughts happen and how the brain amplifies them"
              className="w-full border border-[color:var(--border)] px-2 py-1 text-sm focus:outline-none focus:border-[color:var(--primary)]"
              data-testid={`mc-chapter-${index}-description`}/>
          </label>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-widest mb-1 text-[color:var(--text-muted)]">
              Chapter subtopics (optional) — one per line, render as a bulleted list under the chapter title in the exported book
            </span>
            <textarea
              value={
                Array.isArray(chapter.subtopics)
                  ? chapter.subtopics.join("\n")
                  : (chapter.subtopics || "")
              }
              onChange={(e)=>{
                const lines = e.target.value
                  .split(/\r?\n/)
                  .map((s) => s.replace(/^\s*[•·●○\-*]\s+/, "").trim());
                onChange({ subtopics: lines });
              }}
              rows={4}
              placeholder={"Restrict \u2192 crave \u2192 overeat \u2192 guilt \u2192 repeat\nWhy \u201Cstarting over Monday\u201D becomes a lifelong loop\nThe nervous system\u2019s role in rebound eating"}
              className="w-full border border-[color:var(--border)] px-2 py-1 text-sm focus:outline-none focus:border-[color:var(--primary)] font-mono"
              data-testid={`mc-chapter-${index}-subtopics`}
            />
            <span className="block text-[10px] text-[color:var(--text-muted)] mt-1">
              Bullets and dashes at the start of a line are stripped automatically.
              Leave blank if this chapter has no subtopic breakdown.
            </span>
          </label>
          <div className="block">
            <span className="block text-[10px] uppercase tracking-widest mb-1 text-[color:var(--text-muted)]">
              Chapter content — bold / italic / lists / paste from Google Docs &amp; Word supported
            </span>
            <RichBodyEditor
              value={chapter.content || ""}
              onChange={(html)=>onChange({ content: html })}
              permissive={permissive}
              bookId={bookId}
              testId={`mc-chapter-${index}-content`}
              minHeight={240}
              placeholder="Paste the full chapter body. Bold, italic and headings from Google Docs / Word are preserved."
            />
          </div>
          <div className="flex items-center gap-2 pt-1">
            {!showPresetSave && ((chapter.title || chapter.content || chapter.description || "").trim()) && (
              <button type="button"
                onClick={()=>{ setPresetName(chapter.title || ""); setShowPresetSave(true); }}
                className="text-xs inline-flex items-center gap-1 text-[color:var(--primary)] hover:underline"
                data-testid={`mc-chapter-${index}-save-preset-btn`}>
                <Save className="w-3 h-3"/> Save as chapter preset (reuse in future books)
              </button>
            )}
            {showPresetSave && (
              <div className="flex items-center gap-2 flex-1" data-testid={`mc-chapter-${index}-save-preset-form`}>
                <input value={presetName} onChange={(e)=>setPresetName(e.target.value)}
                  placeholder="Preset name (e.g. Standard opening chapter)"
                  className="flex-1 border border-[color:var(--border)] px-2 py-1 text-xs focus:outline-none focus:border-[color:var(--primary)]"
                  data-testid={`mc-chapter-${index}-preset-name`}/>
                <button type="button" onClick={handleSavePreset}
                  className="text-xs px-2 py-1 bg-[color:var(--primary)] text-white"
                  data-testid={`mc-chapter-${index}-save-preset-confirm`}>Save</button>
                <button type="button" onClick={()=>setShowPresetSave(false)}
                  className="text-xs px-2 py-1 border border-[color:var(--border)]">Cancel</button>
              </div>
            )}
            {/* iter-107 — Prominent "Delete row" button in the expanded
                card footer. The header-row trash icon is easy to miss
                on narrow layouts (especially for accidentally-added
                Preface / Appendix rows the author wants to remove
                right after creating). This labeled button lives right
                next to the RichBodyEditor so it's discoverable without
                scrolling. Same guardrails as the header trash: locked
                rows must be unlocked first, and a confirm dialog
                gates the actual removal. */}
            <div className="ml-auto">
              <button type="button"
                onClick={()=>{
                  if (locked) {
                    toast.error("Unlock this row first to delete it.");
                    return;
                  }
                  const label = kind === "preface"  ? "preface"
                              : kind === "appendix" ? "appendix"
                              : kind === "part"     ? "part divider"
                              : kind === "section"  ? "section"
                              : "chapter";
                  if (window.confirm(`Delete this ${label}? This can't be undone.`)) onRemove();
                }}
                disabled={locked}
                className={`text-xs inline-flex items-center gap-1 px-2 py-1 border ${
                  locked
                    ? "border-slate-200 text-slate-400 cursor-not-allowed"
                    : "border-red-200 text-red-600 hover:bg-red-50"
                }`}
                title={locked ? "Unlock this row first to delete it" : "Delete this row (Preface / Appendix / Chapter)"}
                data-testid={`mc-chapter-${index}-delete-inline`}>
                <Trash2 className="w-3 h-3"/>
                Delete {kind === "preface" ? "preface" : kind === "appendix" ? "appendix" : kind === "part" ? "part" : kind === "section" ? "section" : "chapter"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Small chapter-preset dropdown — separate from the front/back SectionCard
// PresetPicker because chapter presets don't have per-key exact matches.
function ChapterPresetPicker({ testId, presets, onPick, onDelete }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button type="button" onClick={()=>setOpen(!open)}
        className="text-xs inline-flex items-center gap-1 px-2 py-1 border border-[color:var(--border)] hover:bg-[color:var(--canvas)] whitespace-nowrap"
        data-testid={`${testId}-toggle`}>
        <Bookmark className="w-3 h-3"/> Load ({presets.length})
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-20 min-w-[280px] bg-white border border-[color:var(--border)] shadow-lg"
             data-testid={`${testId}-menu`}>
          <div className="p-1">
            <p className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] px-2 py-1">Chapter presets</p>
            {presets.map((p) => (
              <div key={p.id} className="group flex items-center justify-between px-2 py-1 hover:bg-[color:var(--canvas)]" data-testid={`${testId}-preset-${p.id}`}>
                <button type="button" onClick={()=>{ onPick(p); setOpen(false); }}
                  className="text-left flex-1 text-xs min-w-0" data-testid={`${testId}-preset-${p.id}-pick`}>
                  <div className="truncate font-medium">{p.name}</div>
                  <div className="truncate text-[color:var(--text-muted)]">{(p.content || p.description || p.subtitle || "").slice(0, 80) || "(empty body)"}</div>
                </button>
                <button type="button" onClick={()=>onDelete(p.id)}
                  className="p-1 opacity-60 group-hover:opacity-100 text-[color:var(--text-muted)] hover:text-red-600"
                  title="Delete preset" data-testid={`${testId}-preset-${p.id}-delete`}>
                  <Trash2 className="w-3 h-3"/>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
